import { z } from "zod";

const optionalText = z
  .string()
  .nullable()
  .transform((value) => (value && value.trim().length > 0 ? value : null));

export const MenuVariantSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  priceCents: z.number().int().min(0),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});

export const StorefrontMenuVariantSchema = MenuVariantSchema.omit({ isActive: true });

export const MenuFactSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
});

const nullableFlag = z.boolean().nullable();

export const StorefrontMenuItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: optionalText,
  priceCents: z.number().int().min(0),
  imageUrl: optionalText,
  imageAlt: optionalText,
  sortOrder: z.number().int(),
  featured: z.boolean(),
  vegetarian: nullableFlag,
  vegan: nullableFlag,
  spicy: nullableFlag,
  allergens: z.array(MenuFactSchema),
  additives: z.array(MenuFactSchema),
  variants: z.array(StorefrontMenuVariantSchema),
});

export const StorefrontMenuCategorySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: optionalText,
  imageUrl: optionalText,
  imageAlt: optionalText,
  sortOrder: z.number().int(),
  items: z.array(StorefrontMenuItemSchema),
});

export const StorefrontMenuSchema = z.object({
  restaurantId: z.string().min(1),
  categories: z.array(StorefrontMenuCategorySchema),
});

export type StorefrontMenuItem = z.infer<typeof StorefrontMenuItemSchema>;
export type StorefrontMenuCategory = z.infer<typeof StorefrontMenuCategorySchema>;
export type StorefrontMenu = z.infer<typeof StorefrontMenuSchema>;

export const DashboardMenuItemSchema = StorefrontMenuItemSchema.extend({
  categoryId: z.string().min(1),
  isActive: z.boolean(),
  variants: z.array(MenuVariantSchema),
});

export const DashboardMenuCategorySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: optionalText,
  imageUrl: optionalText,
  imageAlt: optionalText,
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  items: z.array(DashboardMenuItemSchema),
});

export const DashboardMenuSchema = z.object({
  restaurantId: z.string().min(1),
  categories: z.array(DashboardMenuCategorySchema),
});

export const DashboardRestaurantSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  logoUrl: optionalText,
  ordersPaused: z.boolean(),
});

export const UpdateRestaurantOrdersPausedBodySchema = z.object({
  ordersPaused: z.boolean(),
  restaurantId: z.string().optional(),
});

export const DashboardLogoUploadSchema = z.object({
  logoUrl: z.string().min(1),
});

export const DashboardImageUploadSchema = z.object({
  imageUrl: z.string().min(1),
});

export const DashboardRestaurantListSchema = z.object({
  restaurants: z.array(DashboardRestaurantSchema),
});

export type DashboardMenuItem = z.infer<typeof DashboardMenuItemSchema>;
export type DashboardMenuCategory = z.infer<typeof DashboardMenuCategorySchema>;
export type DashboardMenu = z.infer<typeof DashboardMenuSchema>;
export type DashboardRestaurant = z.infer<typeof DashboardRestaurantSchema>;
export type DashboardRestaurantList = z.infer<typeof DashboardRestaurantListSchema>;
export type UpdateRestaurantOrdersPausedBody = z.infer<
  typeof UpdateRestaurantOrdersPausedBodySchema
>;
export type DashboardLogoUpload = z.infer<typeof DashboardLogoUploadSchema>;
export type DashboardImageUpload = z.infer<typeof DashboardImageUploadSchema>;

const nullableTextInput = z.union([z.string(), z.null()]).optional();

export const CreateCategoryBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: nullableTextInput,
  imageUrl: nullableTextInput,
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

export const UpdateCategoryBodySchema = CreateCategoryBodySchema.partial();

export const CreateItemBodySchema = z.object({
  categoryId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  description: nullableTextInput,
  priceCents: z.number().int().min(0),
  imageUrl: nullableTextInput,
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

export const UpdateItemBodySchema = CreateItemBodySchema.partial();

export const ReorderCategoriesBodySchema = z.object({
  ids: z.array(z.string().uuid()).min(1),
});

export const ReorderItemsBodySchema = z.object({
  categoryId: z.string().uuid(),
  ids: z.array(z.string().uuid()).min(1),
});

export const ReplaceMenuVariantsBodySchema = z.object({
  variants: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        name: z.string().trim().min(1).max(80),
        priceCents: z.number().int().min(0),
        isActive: z.boolean(),
        sortOrder: z.number().int().min(0),
      }),
    )
    .max(20),
});

export type CreateCategoryBody = z.infer<typeof CreateCategoryBodySchema>;
export type UpdateCategoryBody = z.infer<typeof UpdateCategoryBodySchema>;
export type CreateItemBody = z.infer<typeof CreateItemBodySchema>;
export type UpdateItemBody = z.infer<typeof UpdateItemBodySchema>;
export type ReorderCategoriesBody = z.infer<typeof ReorderCategoriesBodySchema>;
export type ReorderItemsBody = z.infer<typeof ReorderItemsBodySchema>;
export type MenuVariant = z.infer<typeof MenuVariantSchema>;
export type StorefrontMenuVariant = z.infer<typeof StorefrontMenuVariantSchema>;
export type ReplaceMenuVariantsBody = z.infer<typeof ReplaceMenuVariantsBodySchema>;
